import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, RefreshCw, Layers, FileText } from 'lucide-react';
import { toast } from 'sonner';

const StatusTag = ({ a }) => {
  if (a.passed) return <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">Passed</Badge>;
  if (a.maxed_out) return <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">Max reached</Badge>;
  if (a.attempts_used > 0) return <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">In progress</Badge>;
  return <Badge variant="outline">Not attempted</Badge>;
};

const AssessmentRow = ({ title, a, checked, onToggle, indent, icon: Icon }) => (
  <label
    className={`flex items-center gap-2 py-1.5 text-sm ${indent ? 'pl-7' : ''} ${a.attempts_used ? 'cursor-pointer' : 'opacity-60'}`}
    data-testid={`assessment-row-${a.assessment_id}`}
  >
    <Checkbox
      checked={checked}
      disabled={!a.attempts_used}
      onCheckedChange={onToggle}
      data-testid={`assessment-checkbox-${a.assessment_id}`}
    />
    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
    <span className="truncate">{title}</span>
    <span className="ml-auto flex items-center gap-2 shrink-0">
      <span className="text-xs text-muted-foreground" data-testid={`assessment-attempts-${a.assessment_id}`}>
        {a.attempts_used}/{a.max_attempts} attempts
      </span>
      {a.best_score > 0 && <span className="text-xs text-muted-foreground">Best {Math.round(a.best_score)}%</span>}
      <StatusTag a={a} />
    </span>
  </label>
);

/**
 * AssessmentResetDialog - pick module / sub-module assessments of one course and reset only those attempts
 */
const AssessmentResetDialog = ({ api, resetKey, user, course, onClose, onReset }) => {
  const base = `/progress-reset/${resetKey}/users/${user.id}/courses/${course.course_id}/assessments`;
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(base)
      .then(res => setModules(res.data.modules || []))
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load assessments'))
      .finally(() => setLoading(false));
  }, [api, base]);

  const resettable = useMemo(() => modules.flatMap(m => [m.assessment, ...m.sub_modules.map(sm => sm.assessment)])
    .filter(a => a && a.attempts_used > 0), [modules]);
  const allSelected = resettable.length > 0 && resettable.every(a => selected.has(a.assessment_id));

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(resettable.map(a => a.assessment_id)));

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`${base}/reset`, { assessment_ids: [...selected] });
      toast.success(res.data.message, { description: res.data.reset_assessments.join(', ') });
      onReset();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset assessments');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="max-w-2xl bg-background" data-testid="assessment-reset-dialog">
        <DialogHeader>
          <DialogTitle>Reset assessment attempts</DialogTitle>
          <DialogDescription>
            {user.name || user.email} · {course.course_name}. Only the ticked assessments are reset; content progress and all other assessments stay as they are.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-3">
            <label className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium cursor-pointer">
              <Checkbox checked={allSelected} disabled={!resettable.length} onCheckedChange={toggleAll} data-testid="assessment-select-all" />
              Reset all assessments ({resettable.length} with attempts)
            </label>
            <ScrollArea className="h-[320px] rounded-md border px-3 py-2">
              {modules.map(m => (
                <div key={m.id} className="py-1" data-testid={`assessment-module-${m.id}`}>
                  <p className="flex items-center gap-2 text-sm font-semibold py-1">
                    <Layers className="h-4 w-4 text-muted-foreground" /> {m.title}
                  </p>
                  {m.assessment && (
                    <AssessmentRow title="Module assessment" a={m.assessment} icon={FileText} indent
                      checked={selected.has(m.assessment.assessment_id)} onToggle={() => toggle(m.assessment.assessment_id)} />
                  )}
                  {m.sub_modules.filter(sm => sm.assessment).map(sm => (
                    <AssessmentRow key={sm.id} title={sm.title} a={sm.assessment} icon={FileText} indent
                      checked={selected.has(sm.assessment.assessment_id)} onToggle={() => toggle(sm.assessment.assessment_id)} />
                  ))}
                  {!m.assessment && !m.sub_modules.some(sm => sm.assessment) && (
                    <p className="pl-7 text-xs text-muted-foreground py-1">No assessments in this module</p>
                  )}
                </div>
              ))}
            </ScrollArea>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="assessment-reset-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!selected.size || busy} className="bg-amber-600 hover:bg-amber-700" data-testid="assessment-reset-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Reset {selected.size || ''} selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssessmentResetDialog;
